import { SelectData } from '../../../models/common/types';
import { NotificationItem, PermissionItem, User, UserTableFields } from '../../../models/user/user';

export interface CreateUserRequest {
    firstName: string;
    lastName: string;
    password?: string;
    userName: string;
    email: string;
    phoneNumber: string;
    phoneNumberExt: string | null;
    titleRoleID?: number | string;
    projectGroupID: number | null;
    accountRoleID?: number | string;
    projectBuilderRoleID: number;
    notificationSettings: Array<NotificationItem>;
    parentAccountId?: number;
    accountId?: number;
    organizationName?: string;
    organizationSizeId?: number;
    maxUsersCount?: number;
    canAddProductsToOrgFavorites?: boolean | string;
    tagPrivilege?: boolean;
    guidelinePrivilege?: boolean;
}

export interface UpdateUserRequest {
    user: CreateUserRequest;
    id: string;
}

export interface CreateUserResponse {
    user: User;
}

export interface GerUsersResponse {
    data: UserTableFields[];
    usersOnAllPagesCount: number;
    permissions: Permissions;
}

export interface GetUserReferencesResponse {
    allProjectGroups: Array<SelectData>;
    allAccountRoles: Array<SelectData>;
    allTitleRoles: Array<SelectData>;
}

export interface ChangeUserProjectBuilderRoleRequest {
    projectBuilderRoleID: string;
    userIds: Array<string>;
}

export interface ChangeUserAccountRoleRequest {
    userIds: Array<string>;
    accountRoleID: number;
}

export enum ActionResultType {
    Success = 'success',
    Warning = 'warning',
    Error = 'error',
}

export interface ChangeAccountRoleResponse {
    message: string;
    resultType: ActionResultType;
}

export interface ChangeUserProjectGroupRequest {
    projectGroupID: string;
    userIds: Array<string>;
}

export interface CreateUserInBulkRequest {
    users: Array<CreateUserRequest>;
}

export interface UserByIdResponse {
    accountCreated: string;
    accountId: number;
    accountUser: Array<{
        accountId: number;
        accountUserId: number;
        accountRole: { id: number; name: string };
        subscription: { id: number; planName: string; maxUserCount?: number; maxAssociateUserCount?: number };
        titleRoleName: string;
        canAddProductsToOrgFavorites: boolean;
        tagPrivilege: boolean;
        guidelinePrivilege: boolean;
    }>;
    email: string;
    firstName: string;
    id: number;
    isMemberOfOtherAccount: boolean;
    lastName: string;
    notificationSettings: Array<NotificationItem>;
    parentAccountId: number;
    phoneNumber: string;
    phoneNumberExt: null;
    projectBuilderRoleID: number;
    projectBuilderRoleName: string;
    projectGroupID: number | null;
    projectGroupName: string;
    subscriptionPlanId: number | null;
    subscriptionPlanName: string | null;
    titleRoleID: number;
    titleRoleName: string;
    userName: string;
    accountRoleID: number;
    userRoles: Array<UserRole>;
    permission: Array<PermissionItem>;
    subscription: SubscriptionPlan;
    organization: SelectData & OrganizationSize;
    office: SelectData;
    usedTrialSubscription: boolean;
}

interface OrganizationSize {
    size: SelectData;
}

export interface SubscriptionPlan {
    id: number;
    planId: number;
    planName: string;
    name: string;
    expiredOn: string;
}

interface UserRole {
    role: Role;
}

interface Role {
    name: string;
}

export interface AddProjectGroupResponse {
    data: {
        id: number;
        name: string;
        createdOn: string;
        createdById: number;
        modifiedOn: string | null;
        modifiedById: number | null;
    };
}

export interface AddProjectGroupRequest {
    name: string;
}

export interface RemoveProjectGroupRequest {
    id: string;
}

export interface ChangePasswordRequest {
    oldPassword: string;
    password: string;
    confirmPassword: string;
}

export interface GetAccountSettingsResponse {
    payload: {
        data: {
            accountId: number;
            contactName: string;
            email: string;
            services: Array<{ id: number; name: string; num: number; text?: string }>;
            phone: string;
            phoneExt: string;
            organization: {
                id: number;
                name: string;
                sizeId: number;
                sizeName: string;
            };
            office?: {
                address1: string;
                address2: string;
                city: string;
                country?: {
                    id: number;
                    name: string;
                };
                id: number;
                organization: string;
                state?: {
                    id: number;
                    name: string;
                    country?: {
                        id: number;
                        name: string;
                    };
                };
            };
            subscription?: {
                accountId: number;
                maxAssociateUserCount: number;
                maxUserCount: number;
                nextRenewOn: string;
                paymentMethod: string;
                planId: number;
                planName: string;
                renewedOn: string;
                userInUse: number;
                status?: string;
                cancelAtPeriodEnd?: boolean;
            };
        };
    };
}

export interface UpdateAccountSettingsRequest {
    services: Array<{ id: number; text?: string }>;
    organizationName: string;
    organizationSizeId: number;
    organizationSizeName?: string;
    officeStateId: number;
    officeCity: string;
    officeId?: number;
}

export interface GetSubscriptionAdminResponse {
    payload: {
        data: {
            firstName: string,
            lastName: string,
            email: string,
            phoneNumber: string | null,
            phoneNumberExt: string | null
        }
    }
}